import { UUID } from '@cmg/common';
import { DomainNav } from '@cmg/design-system';
import { xcSelectors } from '@cmg/e2e-selectors';
import { FC } from 'react';
import { useLocation } from 'react-router-dom';

import routeFactory, { findActiveRouteGroup } from '../../../../common/util/routeFactory';
import { ReactRouterDomainNavTab } from '../../../../design-system/react-router/ReactRouterDomainNavTab';
import { OfferingStage, OfferingStatus, OfferingType } from '../../../../graphql';
import {
  useFinalSettlementRoute,
  useOfferingDetailsRoute,
  useOrderBookRoute,
  useRegulatoryFilingsRoute,
  useSalesCreditsRoute,
  useSyndicateWiresRoute,
  useTradeReleaseRoute,
} from './hooks';
import { usePrivatePlacementDemandRoute } from './hooks/usePrivatePlacementDemandRoute';
import { useWallCrossRoute } from './hooks/useWallCrossRoute';

export type Props = {
  offeringId?: UUID;
  offeringStage: OfferingStage;
  isAuthor: boolean;
  isInternal: boolean;
  offeringType?: OfferingType;
  offeringStatus?: OfferingStatus;
  isSharedDraft: boolean;
  isDiscarded: boolean;
  isWallCrossEnabled?: boolean;
  offeringCountryCode?: string;
};

/**
 * OfferingNavbar displays a sub-navigation bar on the offerings view
 * displays navigation items for details, order-book
 */
export const OfferingNavbar: FC<Props> = ({
  offeringId,
  offeringStage,
  isAuthor,
  isInternal,
  offeringType,
  offeringStatus,
  isSharedDraft,
  isDiscarded,
  isWallCrossEnabled,
  offeringCountryCode,
}) => {
  const { pathname } = useLocation();
  const activeRouteGroup = findActiveRouteGroup(pathname, {
    'offering-detail': [
      routeFactory.offeringCreate.routePath,
      routeFactory.offeringSetup.routePath,
      routeFactory.offeringSetupNext.routePath,
      routeFactory.offeringDetails.routePath,
      routeFactory.sharedDraftOffering.routePath,
    ],
    'wall-cross': routeFactory.wallCross.routePath,
    'order-book': routeFactory.orderBook.routePath,
    'private-placement-demand': routeFactory.privatePlacementDemand.routePath,
    trade: routeFactory.tradeRelease.routePath,
    'reg-m': routeFactory.regulatoryFilings.routePath,
    wires: routeFactory.syndicateWires.routePath,
    'final-settlement': routeFactory.finalSettlement.routePath,
    'sales-credits': routeFactory.salesCredits.routePath,
  });
  const wallCrossRoute = useWallCrossRoute({ offeringId, isWallCrossEnabled, isDiscarded });
  const orderBookRoute = useOrderBookRoute({
    offeringId,
    offeringStatus: offeringStatus,
    offeringStage: offeringStage,
    offeringType,
  });
  const privatePlacementDemandRoute = usePrivatePlacementDemandRoute({
    offeringId,
    offeringType,
    offeringStatus,
  });
  const offeringDetailRoute = useOfferingDetailsRoute({
    isAuthor,
    offeringStage,
    offeringId,
    isSharedDraft,
  });
  const regulatoryFilingsRoute = useRegulatoryFilingsRoute({
    offeringId,
    isDiscarded,
    isSharedDraft,
    offeringCountryCode,
  });
  const wiresRoute = useSyndicateWiresRoute({
    offeringId,
    isDiscarded,
    isSharedDraft,
    offeringType,
    offeringCountryCode,
  });
  const tradeReleaseRoute = useTradeReleaseRoute({
    offeringId,
    offeringStatus,
    offeringStage,
  });
  const finalSettlementRoute = useFinalSettlementRoute({
    isAuthor,
    offeringId,
    offeringStage,
    offeringStatus,
    isInternal,
    offeringCountryCode,
  });
  const salesCreditsRoute = useSalesCreditsRoute({
    offeringId,
    offeringStatus,
    offeringStage,
    isInternal,
    isAuthor,
    offeringCountryCode,
  });

  return (
    <DomainNav
      value={activeRouteGroup?.[0]}
      data-testid={xcSelectors.offeringNavbar.testId}
      role="navigation"
      aria-roledescription="offering navbar"
    >
      {offeringDetailRoute && (
        <ReactRouterDomainNavTab {...offeringDetailRoute} value="offering-detail" />
      )}
      {wallCrossRoute && <ReactRouterDomainNavTab {...wallCrossRoute} value="wall-cross" />}
      {orderBookRoute && <ReactRouterDomainNavTab {...orderBookRoute} value="order-book" />}
      {privatePlacementDemandRoute && (
        <ReactRouterDomainNavTab
          {...privatePlacementDemandRoute}
          value="private-placement-demand"
        />
      )}
      {tradeReleaseRoute && <ReactRouterDomainNavTab {...tradeReleaseRoute} value="trade" />}
      {regulatoryFilingsRoute && (
        <ReactRouterDomainNavTab {...regulatoryFilingsRoute} value="reg-m" />
      )}
      {wiresRoute && <ReactRouterDomainNavTab {...wiresRoute} value="wires" />}
      {finalSettlementRoute && (
        <ReactRouterDomainNavTab {...finalSettlementRoute} value="final-settlement" />
      )}
      {salesCreditsRoute && (
        <ReactRouterDomainNavTab {...salesCreditsRoute} value="sales-credits" />
      )}
    </DomainNav>
  );
};
